'use client'

import React from 'react'
import {
  FileText,
  Quote,
  ScanSearch,
  Waypoints,
  BarChart3,
  ShieldCheck,
  BrainCircuit,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Wrench,
  Sparkles,
} from 'lucide-react'

/**
 * Radar V2 Blog Diagrams
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 * New tool colors: Cyan #06b6d4, Purple #a855f7, Teal #14b8a6, Red #ef4444
 */

// ── 1. Platform Overview: Two Camps + Radar Bridge ──────────────────────────

export function SourceInfluenceMockup() {
  const sources = [
    {
      rank: 1,
      domain: 'competitor-a.com',
      citations: 7,
      providers: ['ChatGPT', 'Claude', 'Perplexity', 'Gemini'],
      color: '#F90B8A',
    },
    {
      rank: 2,
      domain: 'industry-blog.io',
      citations: 5,
      providers: ['ChatGPT', 'Perplexity', 'Gemini'],
      color: '#f97316',
    },
    {
      rank: 3,
      domain: 'techreview.com',
      citations: 4,
      providers: ['ChatGPT', 'Claude'],
      color: '#FDC304',
    },
    {
      rank: 4,
      domain: 'your-domain.com',
      citations: 2,
      providers: ['Perplexity'],
      color: '#008dcc',
    },
    {
      rank: 5,
      domain: 'niche-guide.org',
      citations: 1,
      providers: ['Gemini'],
      color: '#475569',
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex items-center gap-2 mb-4'>
          <Waypoints size={16} style={{ color: '#06b6d4' }} />
          <span className='text-sm font-bold text-foreground'>
            Source Influence Map
          </span>
          <span
            className='text-xs px-2 py-0.5 rounded-md'
            style={{ backgroundColor: '#06b6d415', color: '#06b6d4' }}
          >
            Preview
          </span>
        </div>
        <div className='space-y-2'>
          {sources.map(src => (
            <div
              key={src.domain}
              className='bg-background border border-border/60 rounded-lg px-4 py-3 flex items-center gap-3'
            >
              <span className='w-6 h-6 rounded-md flex items-center justify-center text-xs font-black bg-muted text-muted-foreground'>
                {src.rank}
              </span>
              <div className='flex-1 min-w-0'>
                <p className='text-xs font-semibold text-foreground truncate'>
                  {src.domain}
                </p>
                <div className='flex gap-1 mt-1'>
                  {src.providers.map(p => (
                    <span
                      key={p}
                      className='text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground'
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className='text-right flex-shrink-0'>
                <span
                  className='text-sm font-black'
                  style={{ color: src.color }}
                >
                  {src.citations}x
                </span>
                <p className='text-[9px] text-muted-foreground'>cited</p>
              </div>
            </div>
          ))}
        </div>
        <p className='text-[10px] text-muted-foreground mt-3 italic'>
          Your domain ranks #4. Top 3 sources are shaping AI narratives in your
          category.
        </p>
      </div>
    </div>
  )
}

// ── 3. SOV Dashboard Mockup ─────────────────────────────────────────────────

export function SOVDashboardMockup() {
  const competitors = [
    { name: 'Competitor A', sov: 32, rank: 1 },
    { name: 'Competitor B', sov: 24, rank: 2 },
    { name: 'Your Brand', sov: 18, rank: 3 },
    { name: 'Competitor C', sov: 14, rank: 4 },
    { name: 'Competitor D', sov: 8, rank: 5 },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex items-center gap-2 mb-4'>
          <BarChart3 size={16} style={{ color: '#a855f7' }} />
          <span className='text-sm font-bold text-foreground'>
            Prompt Share of Voice
          </span>
          <span
            className='text-xs px-2 py-0.5 rounded-md'
            style={{ backgroundColor: '#a855f715', color: '#a855f7' }}
          >
            Preview
          </span>
        </div>

        <div className='grid grid-cols-3 gap-3 mb-5'>
          <div className='bg-background border border-border/60 rounded-lg p-3 text-center'>
            <p className='text-2xl font-black' style={{ color: '#a855f7' }}>
              #3
            </p>
            <p className='text-[10px] text-muted-foreground'>Your Rank</p>
          </div>
          <div className='bg-background border border-border/60 rounded-lg p-3 text-center'>
            <p className='text-2xl font-black' style={{ color: '#a855f7' }}>
              18%
            </p>
            <p className='text-[10px] text-muted-foreground'>Your SOV</p>
          </div>
          <div className='bg-background border border-border/60 rounded-lg p-3 text-center'>
            <p className='text-2xl font-black text-foreground'>5</p>
            <p className='text-[10px] text-muted-foreground'>Brands Found</p>
          </div>
        </div>

        <div className='space-y-2.5'>
          {competitors.map(c => {
            const isYou = c.name === 'Your Brand'
            return (
              <div key={c.name} className='flex items-center gap-3'>
                <span className='text-xs font-mono font-bold w-6 text-right text-muted-foreground'>
                  #{c.rank}
                </span>
                <span
                  className={`text-xs flex-1 truncate ${isYou ? 'font-bold' : ''}`}
                  style={{ color: isYou ? '#a855f7' : undefined }}
                >
                  {c.name}
                  {isYou && ' (you)'}
                </span>
                <div className='w-32 h-2 rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full'
                    style={{
                      width: `${c.sov * 2.5}%`,
                      backgroundColor: isYou ? '#a855f7' : '#475569',
                    }}
                  />
                </div>
                <span
                  className='text-xs font-mono font-bold w-8 text-right'
                  style={{ color: isYou ? '#a855f7' : undefined }}
                >
                  {c.sov}%
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── 4. Hallucination Flags Mockup ───────────────────────────────────────────

export function HallucinationFlagsMockup() {
  const flags = [
    {
      severity: 'high',
      provider: 'ChatGPT',
      category: 'pricing',
      claim: 'Pricing starts at $149/month for the basic plan',
      reality: 'Pricing starts at $49/month (Starter plan)',
    },
    {
      severity: 'medium',
      provider: 'Gemini',
      category: 'product',
      claim: 'Offers a mobile app for iOS and Android',
      reality: 'No mobile app exists. Web platform only.',
    },
    {
      severity: 'low',
      provider: 'Claude',
      category: 'founding',
      claim: 'Founded in 2023',
      reality: 'Founded in 2024',
    },
  ]

  const severityColors: Record<string, string> = {
    high: '#F90B8A',
    medium: '#FDC304',
    low: '#22c55e',
  }

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex items-center gap-2 mb-4'>
          <BrainCircuit size={16} style={{ color: '#ef4444' }} />
          <span className='text-sm font-bold text-foreground'>
            Hallucination Detection
          </span>
          <span
            className='text-xs px-2 py-0.5 rounded-md'
            style={{ backgroundColor: '#ef444415', color: '#ef4444' }}
          >
            Preview
          </span>
        </div>

        <div className='grid grid-cols-3 gap-3 mb-5'>
          {[
            { label: 'High', count: 1, color: '#F90B8A' },
            { label: 'Medium', count: 1, color: '#FDC304' },
            { label: 'Low', count: 1, color: '#22c55e' },
          ].map(s => (
            <div
              key={s.label}
              className='bg-background border border-border/60 rounded-lg p-3 text-center'
            >
              <p className='text-xl font-black' style={{ color: s.color }}>
                {s.count}
              </p>
              <p className='text-[10px] text-muted-foreground'>
                {s.label} Severity
              </p>
            </div>
          ))}
        </div>

        <div className='space-y-3'>
          {flags.map((flag, i) => (
            <div
              key={i}
              className='bg-background border border-border/60 rounded-lg p-4'
            >
              <div className='flex items-center gap-2 mb-2'>
                <span
                  className='text-[10px] font-bold px-2 py-0.5 rounded'
                  style={{
                    backgroundColor: `${severityColors[flag.severity]}15`,
                    color: severityColors[flag.severity],
                  }}
                >
                  {flag.severity}
                </span>
                <span className='text-[10px] text-muted-foreground'>
                  {flag.provider} / {flag.category}
                </span>
              </div>
              <div className='flex items-start gap-2 mb-1.5'>
                <XCircle
                  size={12}
                  className='mt-0.5 flex-shrink-0'
                  style={{ color: '#F90B8A' }}
                />
                <p className='text-xs text-foreground/80'>
                  AI claims: "{flag.claim}"
                </p>
              </div>
              <div className='flex items-start gap-2'>
                <CheckCircle2
                  size={12}
                  className='mt-0.5 flex-shrink-0'
                  style={{ color: '#22c55e' }}
                />
                <p className='text-xs text-foreground/80'>
                  Reality: "{flag.reality}"
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── 5. Sentiment Analysis Mockup ────────────────────────────────────────────

export function SentimentAnalysisMockup() {
  const providers = [
    {
      name: 'ChatGPT',
      tone: 'positive',
      confidence: 0.87,
      signals: ['innovative', 'market leader'],
      frame: 'Industry standard',
      color: '#22c55e',
    },
    {
      name: 'Claude',
      tone: 'cautionary',
      confidence: 0.72,
      signals: ['reliable', 'but consider alternatives'],
      frame: 'Established with caveats',
      color: '#FDC304',
    },
    {
      name: 'Perplexity',
      tone: 'positive',
      confidence: 0.91,
      signals: ['recommended', 'comprehensive'],
      frame: 'Top recommendation',
      color: '#22c55e',
    },
    {
      name: 'Gemini',
      tone: 'neutral',
      confidence: 0.45,
      signals: ['available option'],
      frame: 'One of several',
      color: '#94a3b8',
    },
  ]

  const toneColors: Record<string, string> = {
    positive: '#22c55e',
    cautionary: '#FDC304',
    neutral: '#94a3b8',
    negative: '#F90B8A',
  }

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex items-center gap-2 mb-4'>
          <Sparkles size={16} style={{ color: '#8b5cf6' }} />
          <span className='text-sm font-bold text-foreground'>
            AI Sentiment Analysis
          </span>
          <span
            className='text-xs px-2 py-0.5 rounded-md'
            style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}
          >
            Enhanced
          </span>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          {providers.map(p => (
            <div
              key={p.name}
              className='bg-background border border-border/60 rounded-lg p-4'
            >
              <div className='flex items-center justify-between mb-3'>
                <span className='text-xs font-bold text-foreground'>
                  {p.name}
                </span>
                <span
                  className='text-[10px] font-bold px-2 py-0.5 rounded'
                  style={{
                    backgroundColor: `${toneColors[p.tone]}15`,
                    color: toneColors[p.tone],
                  }}
                >
                  {p.tone}
                </span>
              </div>
              <div className='mb-2'>
                <div className='flex items-center justify-between mb-1'>
                  <span className='text-[10px] text-muted-foreground'>
                    Confidence
                  </span>
                  <span className='text-[10px] font-mono font-bold text-foreground'>
                    {Math.round(p.confidence * 100)}%
                  </span>
                </div>
                <div className='h-1.5 rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full'
                    style={{
                      width: `${p.confidence * 100}%`,
                      backgroundColor: toneColors[p.tone],
                    }}
                  />
                </div>
              </div>
              <div className='flex flex-wrap gap-1 mb-2'>
                {p.signals.map(s => (
                  <span
                    key={s}
                    className='text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground'
                  >
                    "{s}"
                  </span>
                ))}
              </div>
              <p className='text-[10px] text-muted-foreground'>
                Narrative:{' '}
                <span className='text-foreground/80 font-medium'>
                  {p.frame}
                </span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── 6. Competitive Comparison Infographic ───────────────────────────────────

export function CrossToolConflictDiagram() {
  const conflicts = [
    {
      toolA: {
        name: 'Hallucination Check',
        icon: BrainCircuit,
        color: '#ef4444',
        finding: 'Wrong pricing on ChatGPT',
      },
      toolB: {
        name: 'llms.txt Validator',
        icon: FileText,
        color: '#22c55e',
        finding: 'No llms.txt file found',
      },
      insight:
        'The engines state wrong facts about you. Correct them on your own pages and in the sources the engines cite; no engine documents reading llms.txt.',
      insightType: 'conflict' as const,
    },
    {
      toolA: {
        name: 'Prompt SOV',
        icon: BarChart3,
        color: '#a855f7',
        finding: '18% SOV, rank #3',
      },
      toolB: {
        name: 'Citation Tracker',
        icon: Quote,
        color: '#F90B8A',
        finding: '12% citation rate',
      },
      insight:
        'You are named in category prompts but not in brand-specific answers. Check how your own pages answer direct questions about you.',
      insightType: 'conflict' as const,
    },
    {
      toolA: {
        name: 'Schema Audit',
        icon: ShieldCheck,
        color: '#14b8a6',
        finding: 'Missing Speakable schema',
      },
      toolB: {
        name: 'AEO Auditor',
        icon: ScanSearch,
        color: '#7c3aed',
        finding: 'AEO score 78/100',
      },
      insight:
        'Content is well-structured but its structured data is incomplete. Worth fixing; no engine documents schema as a citation requirement.',
      insightType: 'conflict' as const,
    },
  ]

  return (
    <div data-blog-layout='narrow' className='my-8'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex items-center gap-2 mb-5'>
          <Wrench size={16} style={{ color: '#008dcc' }} />
          <span className='text-sm font-bold text-foreground'>
            Cross-Tool Intelligence
          </span>
          <span
            className='text-xs px-2 py-0.5 rounded-md'
            style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
          >
            Unique to Radar
          </span>
        </div>

        <div className='space-y-4'>
          {conflicts.map((c, i) => {
            const IconA = c.toolA.icon
            const IconB = c.toolB.icon
            return (
              <div
                key={i}
                className='bg-background border border-border/60 rounded-xl p-4'
              >
                {/* Two tool findings */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
                  <div className='rounded-lg p-3 bg-muted'>
                    <div className='flex items-center gap-2 mb-1'>
                      <IconA size={12} style={{ color: c.toolA.color }} />
                      <span className='text-[10px] font-bold text-foreground'>
                        {c.toolA.name}
                      </span>
                    </div>
                    <p className='text-xs text-foreground/80'>
                      {c.toolA.finding}
                    </p>
                  </div>
                  <div className='rounded-lg p-3 bg-muted'>
                    <div className='flex items-center gap-2 mb-1'>
                      <IconB size={12} style={{ color: c.toolB.color }} />
                      <span className='text-[10px] font-bold text-foreground'>
                        {c.toolB.name}
                      </span>
                    </div>
                    <p className='text-xs text-foreground/80'>
                      {c.toolB.finding}
                    </p>
                  </div>
                </div>

                {/* Combined insight */}
                <div
                  className='flex items-start gap-2 rounded-lg p-3'
                  style={{
                    backgroundColor: '#f9731608',
                    borderLeft: '3px solid #f97316',
                  }}
                >
                  <AlertTriangle
                    size={14}
                    className='mt-0.5 flex-shrink-0'
                    style={{ color: '#f97316' }}
                  />
                  <div>
                    <p
                      className='text-[10px] font-bold uppercase tracking-wider mb-0.5'
                      style={{ color: '#f97316' }}
                    >
                      Cross-Tool Insight
                    </p>
                    <p className='text-xs text-foreground/80'>{c.insight}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
